import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { promemoriaApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Copy, Mail, CalendarCheck, CheckCircle2, AlertTriangle } from 'lucide-react';
import PassoCard from '../components/promemoria/PassoCard';
import { PASSI, MAIL_ACQUISTI, oggettoMail, testoMail } from '../lib/promemoria';
import { fmtSoldi } from '../lib/acquisti';
import { cn } from '../lib/utils';

const MESI_BREVI = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];

const TIPI_VENDUTO = ['scontrini', 'fatture', 'proforma'];

const BloccoMail = ({ labelMese }) => {
  const oggetto = oggettoMail(labelMese);
  const testo = testoMail();
  const copia = async () => {
    try {
      await navigator.clipboard.writeText(`${oggetto}\n\n${testo}`);
      toast.success('Oggetto e testo della mail copiati');
    } catch {
      toast.error('Copia non riuscita: seleziona e copia manualmente');
    }
  };
  const mailto = `mailto:${MAIL_ACQUISTI.join(',')}?subject=${encodeURIComponent(oggetto)}`
    + `&body=${encodeURIComponent(testo)}`;

  return (
    <div className="space-y-2 rounded-lg bg-secondary/60 p-3">
      <div>
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">A</p>
        <p className="font-medium break-all">{MAIL_ACQUISTI.join(' · ')}</p>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Oggetto</p>
        <p className="font-medium">{oggetto}</p>
      </div>
      <p className="whitespace-pre-line text-muted-foreground">{testo}</p>
      <div className="flex flex-wrap gap-2 pt-1">
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={copia} data-testid="copia-mail-btn">
          <Copy className="w-3.5 h-3.5 mr-1.5" /> Copia testo
        </Button>
        <Button variant="outline" size="sm" className="h-8 text-xs" asChild data-testid="apri-mail-btn">
          <a href={mailto}><Mail className="w-3.5 h-3.5 mr-1.5" /> Apri mail</a>
        </Button>
      </div>
    </div>
  );
};

const dettaglioPasso = (chiave, dati, labelMese) => {
  if (chiave === 'mail_acquisti') return <BloccoMail labelMese={labelMese} />;
  if (chiave === 'acquisti_xml') {
    const f = dati.fatture;
    return f.presente
      ? <p className="text-emerald-700 font-medium">{f.n_documenti} fatture · {fmtSoldi(f.totale)} · {f.n_fornitori} fornitori</p>
      : <p className="text-amber-700 font-medium">Nessuna fattura in archivio per questo mese</p>;
  }
  if (chiave === 'personale') {
    const p = dati.personale;
    return p.presente
      ? <p className="text-emerald-700 font-medium">{fmtSoldi(p.totale)} · {p.ore} ore</p>
      : <p className="text-amber-700 font-medium">Costo del personale non caricato</p>;
  }
  if (chiave === 'bancari') {
    const b = dati.bancari;
    if (!b.presente) return <p className="text-amber-700 font-medium">Costi bancari non caricati</p>;
    return (
      <p className="text-emerald-700 font-medium">
        {fmtSoldi(b.totale)}
        {b.parziale && b.aggiornato_al && (
          <span className="text-muted-foreground font-normal"> · aggiornato al {b.aggiornato_al}</span>
        )}
      </p>
    );
  }
  if (chiave === 'venduto') {
    const v = dati.venduto;
    return (
      <div className="space-y-1">
        <div className="flex flex-wrap gap-1.5">
          {TIPI_VENDUTO.map((t) => {
            const voce = v.per_tipo.find((x) => x.tipo === t);
            return (
              <Badge
                key={t}
                variant="outline"
                className={cn('text-[10px] capitalize', voce ? 'border-emerald-600 text-emerald-700' : 'border-amber-500 text-amber-700')}
              >
                {t} {voce ? `· ${voce.documenti}` : '· mancanti'}
              </Badge>
            );
          })}
        </div>
        {v.presente && (
          <p className="text-muted-foreground">{v.giornate} giornate · {fmtSoldi(v.incasso)}</p>
        )}
      </div>
    );
  }
  return null;
};

const PromemoriaPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin' || user?.is_super_admin;
  const [dati, setDati] = useState(null);
  const [anno, setAnno] = useState(null);
  const [mese, setMese] = useState(null);
  const [caricando, setCaricando] = useState(true);
  const [salvando, setSalvando] = useState(null);

  const carica = useCallback(async (a, meseDaTenere) => {
    setCaricando(true);
    try {
      const res = await promemoriaApi.lista(a);
      setDati(res);
      setAnno(res.anno);
      const disponibili = res.mesi.map((m) => m.mese);
      setMese(
        disponibili.includes(meseDaTenere) ? meseDaTenere
          : disponibili.includes(res.mese_consigliato) ? res.mese_consigliato
            : disponibili[0] || null,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel caricamento del promemoria');
    } finally {
      setCaricando(false);
    }
  }, []);

  useEffect(() => { carica(null, null); }, [carica]);

  const corrente = dati?.mesi.find((m) => m.mese === mese) || null;

  const spunta = async (chiave, fatto) => {
    setSalvando(chiave);
    try {
      await promemoriaApi.spunta(mese, chiave, fatto);
      await carica(anno, mese);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio non riuscito');
    } finally {
      setSalvando(null);
    }
  };

  return (
    <div className="min-h-screen pb-24">
      <header className="glass-header sticky top-0 z-40 px-4 py-4 border-b border-border">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/impostazioni')} data-testid="promemoria-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl font-bold font-heading tracking-tight">Promemoria mensile</h1>
            <p className="text-xs text-muted-foreground truncate">
              Da fare a inizio mese sui dati del mese appena concluso
            </p>
          </div>
        </div>
      </header>

      <main className="px-4 py-4 max-w-lg mx-auto space-y-4">
        {caricando && !dati && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
          </div>
        )}

        {dati && (
          <>
            <Card>
              <CardContent className="p-3 space-y-2">
                <div className="flex gap-1.5 overflow-x-auto pb-0.5" data-testid="promemoria-anni">
                  {dati.anni_disponibili.map((a) => (
                    <button
                      key={a}
                      onClick={() => carica(a, null)}
                      data-testid={`promemoria-anno-${a}`}
                      className={cn(
                        'shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition-colors',
                        a === anno ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground',
                      )}
                    >
                      {a}
                    </button>
                  ))}
                </div>
                <div className="flex gap-1.5 overflow-x-auto pb-0.5" data-testid="promemoria-mesi">
                  {dati.mesi.map((m) => (
                    <button
                      key={m.mese}
                      onClick={() => setMese(m.mese)}
                      data-testid={`promemoria-mese-${m.mese}`}
                      className={cn(
                        'shrink-0 px-3 py-1 rounded-full text-xs font-semibold capitalize transition-colors flex items-center gap-1',
                        m.mese === mese
                          ? 'bg-primary text-primary-foreground'
                          : m.completo
                            ? 'bg-emerald-500/15 text-emerald-700'
                            : 'bg-secondary text-muted-foreground',
                      )}
                    >
                      {MESI_BREVI[Number(m.mese.slice(5, 7)) - 1]}
                      {m.completo && <CheckCircle2 className="w-3 h-3" />}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {corrente && (
              <Card
                className={corrente.completo ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-primary/40 bg-primary/5'}
                data-testid="promemoria-riepilogo"
              >
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Mese di riferimento</p>
                      <p className="text-2xl font-bold font-heading" data-testid="promemoria-mese-label">{corrente.label}</p>
                    </div>
                    <Badge
                      className={corrente.completo ? 'bg-emerald-600' : 'bg-amber-500'}
                      data-testid="promemoria-stato"
                    >
                      {corrente.completo
                        ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mese completo</>
                        : <><AlertTriangle className="w-3.5 h-3.5 mr-1" /> {corrente.n_passi - corrente.n_fatti} da fare</>}
                    </Badge>
                  </div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div
                      className={cn('h-full rounded-full transition-all', corrente.completo ? 'bg-emerald-600' : 'bg-primary')}
                      style={{ width: `${(corrente.n_fatti / corrente.n_passi) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground" data-testid="promemoria-avanzamento">
                    {corrente.n_fatti} di {corrente.n_passi} passi completati
                    {corrente.mese === dati.mese_consigliato && ' · è il mese appena concluso'}
                  </p>
                  {!isAdmin && (
                    <p className="text-[11px] text-muted-foreground">
                      Le spunte manuali possono essere messe solo dagli amministratori.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {corrente && PASSI.map((p, i) => (
              <PassoCard
                key={p.chiave}
                passo={p}
                numero={i + 1}
                fatto={corrente.passi[p.chiave]}
                dettaglio={dettaglioPasso(p.chiave, corrente.dati, corrente.label)}
                onSpunta={(v) => spunta(p.chiave, v)}
                salvando={salvando === p.chiave}
                isAdmin={isAdmin}
              />
            ))}

            <Card className="bg-secondary/40">
              <CardContent className="p-4 text-xs text-muted-foreground flex items-start gap-2">
                <CalendarCheck className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                <p>
                  Quando tutti i passi sono spuntati il mese è completo e i numeri della dashboard
                  (entrate, uscite, utile o perdita) sono confrontabili con gli altri mesi.
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default PromemoriaPage;
